import { Modal, Select, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../../components/Input/TextArea.tsx"

const MIN_SENTENCE_LENGTH = 1
const MAX_SENTENCE_LENGTH = 500

const REASONS = ["inaccurate", "outdated", "inappropriate", "other"] as const

export interface ReportIssueSubmission {
  sentence: string
  reason: string
}

interface ReportIssueModalProps {
  open: boolean
  submitting?: boolean
  onCancel: () => void
  onSubmit: (body: ReportIssueSubmission) => void
}

/**
 * Screen 2/4's "Report issue" modal — a sentence (1-500 chars) plus a reason,
 * posted as `sendFeedback({kind: "issue", sentence, reason})` by the caller.
 * The caller owns the API call and which edition it targets (Screen 2's
 * footer reports the active edition; Screen 4's history reports whichever
 * edition the reader picked), same division of responsibility as
 * `ProjectTypeConfirm`.
 */
export function ReportIssueModal({
  open,
  submitting,
  onCancel,
  onSubmit
}: ReportIssueModalProps) {
  const { t } = useTranslation()
  const [sentence, setSentence] = useState("")
  const [reason, setReason] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setSentence("")
      setReason(null)
    }
  }, [open])

  const trimmed = sentence.trim()
  const sentenceValid =
    trimmed.length >= MIN_SENTENCE_LENGTH && trimmed.length <= MAX_SENTENCE_LENGTH
  const canSubmit = sentenceValid && !!reason

  const handleSubmit = () => {
    if (!canSubmit || !reason) return
    onSubmit({ sentence: trimmed, reason })
  }

  return (
    <Modal
      open={open}
      title={t("projectEducation.reportIssueModal.title")}
      okText={t("projectEducation.reportIssueModal.submit")}
      cancelText={t("projectEducation.reportIssueModal.cancel")}
      onCancel={onCancel}
      onOk={handleSubmit}
      okButtonProps={{ disabled: !canSubmit, loading: submitting }}
      destroyOnHidden
    >
      <div className={"mb-3"}>
        <label htmlFor={"education-report-issue-sentence"}>
          <Typography.Text>
            {t("projectEducation.reportIssueModal.sentenceLabel")}
          </Typography.Text>
        </label>
        <TextArea
          id={"education-report-issue-sentence"}
          value={sentence}
          rows={3}
          onChange={(event) => setSentence(event.target.value)}
          status={trimmed.length > 0 && !sentenceValid ? "error" : undefined}
        />
        {trimmed.length > 0 && !sentenceValid && (
          <Typography.Text type={"danger"} className={"text-xs"}>
            {t("projectEducation.reportIssueModal.sentenceError")}
          </Typography.Text>
        )}
      </div>

      <div>
        <label htmlFor={"education-report-issue-reason"}>
          <Typography.Text>{t("projectEducation.reportIssueModal.reasonLabel")}</Typography.Text>
        </label>
        <Select
          id={"education-report-issue-reason"}
          className={"w-full"}
          value={reason ?? undefined}
          placeholder={t("projectEducation.reportIssueModal.reasonPlaceholder")}
          onChange={setReason}
          options={REASONS.map((value) => ({
            value,
            label: t(`projectEducation.reportIssueModal.reasons.${value}`)
          }))}
        />
      </div>
    </Modal>
  )
}
